import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getOrganizations } from "../../REDUX/actions/organziation";

/**
 * Who may pick an organization in Espace clients, and from which list:
 * an admin from every organization, a business affiliate from the ones it
 * sponsors. A reseller never picks (the server pins its own organization).
 * Options are plain { name, code } — Select matches its value by equality.
 */
export const usePartnerOrganizations = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.userReducer);
  const { organizations } = useSelector((state) => state.organizationReducer);

  const role = user?.attributes?.userRole;
  const affiliateId = user?.attributes?.business_affiliate?.data?.id;
  const isAdmin = role === 1;
  const isAffiliate = role === 2;

  useEffect(() => {
    if (!isAdmin && !isAffiliate) return;
    const controller = new AbortController();
    dispatch(
      getOrganizations(
        isAffiliate
          ? { business_affiliate: { id: { $eq: affiliateId ?? -1 } } }
          : {},
        controller.signal
      )
    );
    return () => controller.abort();
  }, [dispatch, isAdmin, isAffiliate, affiliateId]);

  const organizationOptions = useMemo(
    () => (organizations ?? []).map((o) => ({ name: o.name, code: o.code })),
    [organizations]
  );

  /** Admin filter bar: the organizations one affiliate sponsors. */
  const optionsForAffiliate = (businessAffiliateId) =>
    businessAffiliateId
      ? organizationOptions.filter(
          (option) =>
            (organizations ?? []).find((o) => o.code === option.code)
              ?.businessAffiliate === businessAffiliateId
        )
      : organizationOptions;

  return {
    isAdmin,
    isAffiliate,
    canPickOrganization: isAdmin || isAffiliate,
    organizationOptions,
    optionsForAffiliate,
  };
};
